/**
 * Etapa 4 — Informações do evento (mockup 20 Estado C, bloco "Identificação").
 *
 * Lead capture obrigatório no fluxo organizacao. Style alinhado com mockup —
 * grid 5-col pra info do evento (mobile colapsa 2-col) + grid 3-col pra contato.
 */
import { useState } from 'react';
import type { EventosOrgSubmitterMeta } from '../api/types';
import { t, type I18nBundle } from '../lib/i18n';
import { NavRow, QBlock } from './QBlock';

interface Props {
  value: EventosOrgSubmitterMeta;
  onChange: (patch: Partial<EventosOrgSubmitterMeta>) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  i18n: I18nBundle;
}

const SEGMENT_KEYS = ['feira', 'festival', 'congresso', 'corporativo', 'outro'] as const;

export function StepOrgInfoEvento({ value, onChange, onBack, onSubmit, submitting, i18n }: Props) {
  const [touched, setTouched] = useState(false);
  const nameOk = value.contact_name.trim().length > 0;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.contact_email);
  const canSubmit = nameOk && emailOk && !submitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (canSubmit) onSubmit();
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <QBlock title={t(i18n, 'org.step4.title')} subtitle={t(i18n, 'org.step4.intro')}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3.5">
          <Field label={t(i18n, 'org.step4.event_name')} colSpan={2}>
            <input
              type="text"
              value={value.event_name ?? ''}
              onChange={(e) => onChange({ event_name: e.target.value })}
              placeholder={t(i18n, 'org.step4.event_name_placeholder')}
              className={INPUT_CLS}
            />
          </Field>
          <Field label={t(i18n, 'org.step4.segment')}>
            <select
              value={value.segment ?? ''}
              onChange={(e) => onChange({ segment: e.target.value })}
              className={INPUT_CLS}
            >
              <option value="">{t(i18n, 'org.step4.segment_select')}</option>
              {SEGMENT_KEYS.map((k) => (
                <option key={k} value={t(i18n, `org.step4.segment_${k}`)}>
                  {t(i18n, `org.step4.segment_${k}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(i18n, 'org.step4.cpf_cnpj')}>
            <input
              type="text"
              value={value.cpf_cnpj ?? ''}
              onChange={(e) => onChange({ cpf_cnpj: e.target.value })}
              placeholder="00.000.000/0000-00"
              className={`${INPUT_CLS} font-mono`}
            />
          </Field>
          <Field label={t(i18n, 'org.step4.state')}>
            <input
              type="text"
              value={value.state ?? ''}
              onChange={(e) => onChange({ state: e.target.value })}
              placeholder="MG"
              maxLength={2}
              className={`${INPUT_CLS} uppercase font-mono`}
            />
          </Field>
          <Field label={t(i18n, 'org.step4.city')} colSpan={4}>
            <input
              type="text"
              value={value.city ?? ''}
              onChange={(e) => onChange({ city: e.target.value })}
              className={INPUT_CLS}
            />
          </Field>
        </div>
      </QBlock>

      <QBlock title={t(i18n, 'org.step4.contact')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
          <Field
            label={t(i18n, 'org.step4.contact_name')}
            required
            error={touched && !nameOk ? t(i18n, 'org.step4.contact_name_err') : undefined}
          >
            <input
              type="text"
              value={value.contact_name}
              onChange={(e) => onChange({ contact_name: e.target.value })}
              required
              className={INPUT_CLS}
            />
          </Field>
          <Field label={t(i18n, 'org.step4.contact_phone')}>
            <input
              type="tel"
              value={value.contact_phone ?? ''}
              onChange={(e) => onChange({ contact_phone: e.target.value })}
              placeholder="(00) 00000-0000"
              className={`${INPUT_CLS} font-mono`}
            />
          </Field>
          <Field
            label={t(i18n, 'org.step4.contact_email')}
            required
            error={touched && !emailOk ? t(i18n, 'org.step4.contact_email_err') : undefined}
          >
            <input
              type="email"
              value={value.contact_email}
              onChange={(e) => onChange({ contact_email: e.target.value })}
              required
              className={INPUT_CLS}
            />
          </Field>
        </div>
      </QBlock>

      <NavRow
        nextIsSubmit
        onBack={onBack}
        backLabel={t(i18n, 'org.common.back')}
        backDisabled={submitting}
        nextLabel={submitting ? t(i18n, 'org.step4.submitting') : t(i18n, 'org.step4.submit')}
        nextDisabled={submitting}
      />
    </form>
  );
}

function Field({
  label,
  required,
  error,
  children,
  colSpan,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  /** col-span no grid 5-col (1..5) ou 3-col (1..3). Default 1. */
  colSpan?: 2 | 3 | 4 | 5;
}) {
  const span =
    colSpan === 2
      ? 'md:col-span-2'
      : colSpan === 3
        ? 'md:col-span-3'
        : colSpan === 4
          ? 'md:col-span-4'
          : colSpan === 5
            ? 'md:col-span-5'
            : '';
  return (
    <label className={'flex flex-col gap-2 ' + span}>
      <span className="font-display font-semibold text-[10.5px] uppercase tracking-[0.18em] text-tabaco">
        {label} {required && <span className="text-cobre">*</span>}
      </span>
      {children}
      {error && <span className="text-2xs text-cobre-700">{error}</span>}
    </label>
  );
}

const INPUT_CLS =
  'block w-full rounded-lg border border-areia bg-cream/60 px-3.5 py-2.5 text-sm text-floresta ' +
  'placeholder:text-ink-subtle focus:outline-none focus:border-bronze focus:bg-cream transition-colors';
